import React, { useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { globalURLS } from '../../Global/urls';

const DEFAULT_EXPIRY_IN_HOURS = 48;
const DEFAULT_MAX_ATTEMPTS = 10;

type BulkUser = {
  accountid?: string;
  phone?: string;
  pin?: string;
  destinationlink: string;
  expiryInHours: number;
  maxAttempts: number;
  host_access?: boolean;
  host_discussionid?: string | null;
};

type BulkResult = BulkUser & {
  link?: string;
  passcode?: string;
  expiry?: string;
  message?: string;
  body?: { message?: string };
};

const parseHostAccess = (value?: string): boolean => {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

const escapeCsvValue = (value: string) => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const resultsToCsv = (results: BulkResult[]) => {
  const headers = [
    'accountid',
    'phone',
    'pin',
    'destinationlink',
    'expiryInHours',
    'maxAttempts',
    'host_access',
    'host_discussionid',
    'link',
    'passcode',
    'expiry',
    'error',
  ];
  const rows = results.map((row) => {
    const error = row.link
      ? ''
      : row.message || row.body?.message || 'Failed to create link';
    return [
      row.accountid || '',
      row.phone || '',
      row.pin || '',
      row.destinationlink || '',
      row.expiryInHours ?? DEFAULT_EXPIRY_IN_HOURS,
      row.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
      row.host_access ? 'true' : 'false',
      row.host_discussionid || '',
      row.link || '',
      row.passcode || '',
      row.expiry || '',
      error,
    ].map(String).map(escapeCsvValue);
  });
  return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
};

const downloadCsv = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const BulkCreateLinks: React.FC = () => {
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const parseUsersFromCsv = (text: string): { users: BulkUser[] } | { error: string } | null => {
    const rows = text.split('\n').filter((row) => row.trim() !== '');
    if (rows.length < 2) return null;

    const headers = rows[0].split(',').map((h) => h.trim().toLowerCase());
    const accountidIdx = headers.indexOf('accountid');
    const phoneIdx = headers.indexOf('phone');
    const pinIdx = headers.indexOf('pin');
    const destinationlinkIdx = headers.indexOf('destinationlink');
    const expiryInHoursIdx = headers.indexOf('expiryinhours');
    const maxAttemptsIdx = headers.indexOf('maxattempts');
    const hostAccessIdx = headers.indexOf('host_access');
    const hostDiscussionIdIdx = headers.indexOf('host_discussionid');

    if (accountidIdx === -1 && phoneIdx === -1) return null;
    if (destinationlinkIdx === -1) return null;

    const users: BulkUser[] = [];
    for (let i = 0; i < rows.slice(1).length; i++) {
      const row = rows[i + 1];
      const values = row.split(',').map((v) => v.trim());
      const accountid = accountidIdx >= 0 ? values[accountidIdx] : undefined;
      const phone = phoneIdx >= 0 ? values[phoneIdx] : undefined;
      const pin = pinIdx >= 0 ? values[pinIdx] : undefined;
      const destinationlink = values[destinationlinkIdx];
      const expiryValue = expiryInHoursIdx >= 0 ? values[expiryInHoursIdx] : '';
      const parsedExpiry = expiryValue ? Number(expiryValue) : DEFAULT_EXPIRY_IN_HOURS;
      const maxAttemptsValue = maxAttemptsIdx >= 0 ? values[maxAttemptsIdx] : '';
      const parsedMaxAttempts = maxAttemptsValue ? Number(maxAttemptsValue) : DEFAULT_MAX_ATTEMPTS;
      const host_access = hostAccessIdx >= 0 ? parseHostAccess(values[hostAccessIdx]) : false;
      const host_discussionid =
        hostDiscussionIdIdx >= 0 ? values[hostDiscussionIdIdx] || null : null;

      if (!accountid && !phone) {
        return {
          error: `Row ${i + 2}: accountid or phone is required.`,
        };
      }

      if (!destinationlink) {
        return {
          error: `Row ${i + 2}: destinationlink is required.`,
        };
      }

      if (expiryValue && (isNaN(parsedExpiry) || parsedExpiry <= 0)) {
        return {
          error: `Row ${i + 2}: expiryInHours must be a positive number.`,
        };
      }

      if (maxAttemptsValue && (isNaN(parsedMaxAttempts) || parsedMaxAttempts <= 0)) {
        return {
          error: `Row ${i + 2}: maxAttempts must be a positive number.`,
        };
      }

      if (host_access && !host_discussionid) {
        return {
          error: `Row ${i + 2}: host_discussionid is required when host_access is true.`,
        };
      }

      users.push({
        accountid: accountid || undefined,
        phone: phone || undefined,
        pin: pin || undefined,
        destinationlink,
        expiryInHours: parsedExpiry,
        maxAttempts: parsedMaxAttempts,
        host_access,
        host_discussionid: host_access ? host_discussionid : null,
      });
    }

    return users.length ? { users } : null;
  };

  const handleBulkSubmit = async () => {
    if (!selectedFile) {
      setModalMessage('Please select a CSV file.');
      setShowMessageModal(true);
      return;
    }

    setIsLoading(true);

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const parsed = parseUsersFromCsv(text);

      if (!parsed) {
        setModalMessage(
          'Invalid CSV format. Include a header row with "destinationlink" (required), "accountid" and/or "phone", and optional "pin", "expiryInHours", "maxAttempts", "host_access", "host_discussionid". Each data row must have accountid or phone, and a destinationlink.'
        );
        setShowMessageModal(true);
        setIsLoading(false);
        return;
      }

      if ('error' in parsed) {
        setModalMessage(parsed.error);
        setShowMessageModal(true);
        setIsLoading(false);
        return;
      }

      const payload = { users: parsed.users };

      try {
        const response = await axios.post(
          `${globalURLS.baseURL}createMultiLoginLinks`,
          payload,
          globalURLS.config
        );

        if (response?.data?.success && Array.isArray(response?.data?.data)) {
          downloadCsv(resultsToCsv(response.data.data), 'magic_links.csv');
          setShowBulkModal(false);
          setSelectedFile(null);
        } else {
          setModalMessage(response?.data?.message || 'Failed to create bulk magic links.');
          setShowMessageModal(true);
        }
      } catch (error: any) {
        setModalMessage(
          error?.response?.data?.message || error?.message || 'Failed to create bulk magic links.'
        );
        setShowMessageModal(true);
      } finally {
        setIsLoading(false);
      }
    };

    reader.onerror = () => {
      setModalMessage('Error reading the file. Please try again.');
      setShowMessageModal(true);
      setIsLoading(false);
    };

    reader.readAsText(selectedFile);
  };

  return (
    <>
      <Button
        variant="outline-dark"
        onClick={() => setShowBulkModal(true)}
        className="mb-4"
      >
        Create Bulk Magic Links
      </Button>

      <Modal show={showBulkModal} onHide={() => !isLoading && setShowBulkModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Bulk Magic Links</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Upload a CSV with a header row and one user per line. Each row must include accountid or phone, and a destinationlink.</p>
          <ul>
            <li><strong>accountid</strong> — user account UUID (accountid or phone required)</li>
            <li><strong>phone</strong> — user phone number (accountid or phone required)</li>
            <li><strong>destinationlink</strong> — destination URL (required)</li>
            <li><strong>expiryInHours</strong> — link expiry in hours (optional, default 48)</li>
            <li><strong>maxAttempts</strong> — max login attempts (optional, default 10)</li>
            <li><strong>pin</strong> — custom PIN (optional)</li>
            <li><strong>host_access</strong> — true/false, 1/0, or yes/no (optional, default false)</li>
            <li><strong>host_discussionid</strong> — required when host_access is true</li>
          </ul>

          <Form.Group controlId="bulkCsvFile" className="mb-3">
            <Form.Label>Select CSV File</Form.Label>
            <Form.Control type="file" accept=".csv" onChange={handleFileChange} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkModal(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="dark"
            onClick={handleBulkSubmit}
            disabled={!selectedFile || isLoading}
          >
            {isLoading ? <Spinner animation="border" size="sm" /> : 'Submit'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showMessageModal} onHide={() => setShowMessageModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMessageModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BulkCreateLinks;
